import { useLogoStore } from "../state/logoStore";
import { renderLogoSVG } from "../engine/render";
import { Field, Slider, ColorRow, FontPicker, slug, gradeClass } from "../components/editor/EditorControls";
import { IdeasPanel } from "../components/editor/IdeasPanel";
import { PreviewPanel } from "../components/editor/PreviewPanel";
import { MoreIdeasPanel } from "../components/editor/MoreIdeasPanel";
import { ScorePanel, CustomIconsPanel, QrCodePanel } from "../components/editor/SidePanels";
import { PaletteFromImage } from "../components/editor/PalettePanels";
import { ContainerPanel } from "../components/editor/ContainerPanel";
import { Style3DPicker, GoldenRatioOverlay } from "../components/editor/Pickers";
import { IconPatternPanel, HistoryPanel, SloganSuggestionsInline } from "../components/editor/SmallPanels";
import { exportPNG, exportSVG, exportBrandKit, exportProject } from "../engine/exporter";
import { relativeLuminance } from "../engine/contrast";
import { ICONS, ICON_CATEGORIES } from "../assets/icons";
import { PageLabel } from "../components/ui/PageLabel";
import { ShortcutsHelp } from "../components/ShortcutsHelp";
import { useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts";
import { buildQrPaths } from "../engine/qrcode";
import { scoreLogo } from "../engine/aestheticScore";
import { useEffect, useMemo, useState } from "react";
import { publishPublicLogo } from "../lib/publicLogos";
import { useAuth } from "../hooks/useAuth";

const LAYOUTS = [
  { id: "icon-top",   label: "Icon top" },
  { id: "icon-left",  label: "Icon left" },
  { id: "icon-right", label: "Icon right" },
  { id: "text-only",  label: "Text only" },
  { id: "icon-only",  label: "Icon only" },
] as const;

type EditorTab =
  | "preview" | "suggested" | "layout" | "palette" | "more-ideas"
  | "background" | "name" | "slogan" | "symbol" | "container" | "history" | "score";

const NAV_SECTIONS: Array<{ id: EditorTab; label: string; glyph: string; title?: string }> = [
  { id: "preview",    label: "Preview",    glyph: "👁" },
  { id: "suggested",  label: "Suggested",  glyph: "💡" },
  { id: "layout",     label: "Layout",     glyph: "▤" },
  { id: "palette",    label: "Palette",    glyph: "💧" },
  { id: "more-ideas", label: "More Ideas", glyph: "✨" },
  { id: "background", label: "Background", glyph: "▦" },
  { id: "name",       label: "Name",       glyph: "A" },
  { id: "slogan",     label: "Slogan",     glyph: "Aa" },
  { id: "symbol",     label: "Symbol",     glyph: "★" },
  { id: "container",  label: "Container",  glyph: "○" },
  { id: "history",    label: "History",    glyph: "↺" },
  { id: "score",      label: "Score",      glyph: "📊", title: "Aesthetic score" },
];

// === PAGE: EDITOR (main design surface — tabs: preview/suggested/layout/palette/more-ideas/background/name/slogan/symbol/container/history/score) ===
export function EditorPage() {
  const s = useLogoStore();
  const [tab, setTab] = useState<EditorTab>("suggested");
  const [iconCat, setIconCat] = useState<typeof ICON_CATEGORIES[number] | "custom" | "qr">("abstract");
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [qrPaths, setQrPaths] = useState<string[]>([]);
  const [goldenRatio, setGoldenRatio] = useState(false);

  // Re-build QR paths whenever qrText changes
  useEffect(() => {
    let cancelled = false;
    if (s.iconId === "__qr__" && s.qrText) {
      buildQrPaths(s.qrText)
        .then((res) => { if (!cancelled) setQrPaths(res.paths); })
        .catch(() => { if (!cancelled) setQrPaths([]); });
    } else {
      setQrPaths([]);
    }
    return () => { cancelled = true; };
  }, [s.iconId, s.qrText]);

  const svg = renderLogoSVG(s, { width: 500, height: 500, customIcons: s.customIcons, qrPaths });
  const liveScore = useMemo(() => scoreLogo({ state: s }), [
    s.title, s.slogan, s.iconId, s.qrText, s.titleColor, s.sloganColor, s.iconColor,
    s.background, s.layout, s.titleSize, s.sloganSize, s.iconScale, s.customIcons,
  ]);

  function saveToLibrary() {
    const name = prompt("Name this design:", s.title || "Untitled logo");
    if (name === null) return;
    s.saveCurrentToLibrary(name);
    alert("Saved to your library. View on the home page.");
  }

  // Save-as-design now opens the public/private chooser modal instead of prompt().
  // Modal state lives below; this just opens it.
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  function saveAsDesign() {
    setSaveModalOpen(true);
  }

  // Keyboard shortcuts
  useKeyboardShortcuts({
    "mod+z":         () => s.undo(),
    "mod+shift+z":   () => s.redo(),
    "mod+s":         () => saveToLibrary(),
    "mod+e":         () => exportBrandKit(s, slug(s.title)),
    "mod+p":         () => exportPNG(s, 1024),
    "mod+shift+s":   () => exportProject(s, `${slug(s.title)}.logoman.json`),
    "1":             () => { s.pushHistory(); s.setStyle({ layout: "icon-top" }); },
    "2":             () => { s.pushHistory(); s.setStyle({ layout: "icon-left" }); },
    "3":             () => { s.pushHistory(); s.setStyle({ layout: "icon-right" }); },
    "4":             () => { s.pushHistory(); s.setStyle({ layout: "text-only" }); },
    "5":             () => { s.pushHistory(); s.setStyle({ layout: "icon-only" }); },
    "?":             () => setShortcutsOpen((v) => !v),
    "shift+/":       () => setShortcutsOpen((v) => !v),
    "escape":        () => { if (shortcutsOpen) setShortcutsOpen(false); else s.setPage("start"); },
  });

  return (
    <div className="page editor">
      <PageLabel name={`EDITOR · ${tab}`} />
      <ShortcutsHelp open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      <header className="topbar">
        <button className="btn pill-nav pill-nav-back" onClick={() => s.setPage("start")}>
          <span className="pill-nav-arrow" aria-hidden="true">←</span>
          <span>Home</span>
        </button>
        <div className="undo-group">
          <button className="btn ghost icon-only-btn" onClick={() => s.undo()} disabled={!s.canUndo()} title="Undo (⌘Z)">↶</button>
          <button className="btn ghost icon-only-btn" onClick={() => s.redo()} disabled={!s.canRedo()} title="Redo (⌘⇧Z)">↷</button>
        </div>
        <h2>Editor</h2>
        <button
          className={`live-score grade-${gradeClass(liveScore.grade)}`}
          onClick={() => setTab("score")}
          title={`Aesthetic score: ${liveScore.scorePercent}/100 (${liveScore.grade}). Click to see breakdown + feedback.`}
        >
          <span className="live-score-grade">{liveScore.grade}</span>
          <span className="live-score-pct">{liveScore.scorePercent}</span>
        </button>
        <button className="btn ghost icon-only-btn" onClick={() => setShortcutsOpen(true)} title="Shortcuts (?)">?</button>
        <div className="export-group">
          <button
            className="btn primary"
            title="Save this design as a template — it appears as a suggestion in the Templates page so it can be re-used or shared"
            onClick={saveAsDesign}
          >
            📌 Save as design
          </button>
          <button className="btn" title="Save to your personal browser library (⌘S)" onClick={saveToLibrary}>💾 Save</button>
          <button className="btn" onClick={() => exportPNG(s, 1024)} title="PNG (⌘P)">PNG</button>
          <button className="btn" onClick={() => exportSVG(s)}>SVG</button>
          <button className="btn" onClick={() => exportBrandKit(s, slug(s.title))} title="Brand Kit ZIP (⌘E)">Brand Kit ZIP</button>
          <button className="btn ghost" onClick={() => exportProject(s, `${slug(s.title)}.logoman.json`)} title="Download source (⌘⇧S)">⬇ Source</button>
        </div>
      </header>

      <div className="editor-body v2">
        {/* Left: vertical icon rail */}
        <nav className="v-rail">
          {NAV_SECTIONS.map((sec) => (
            <button
              key={sec.id}
              className={`v-rail-btn ${tab === sec.id ? "active" : ""}`}
              onClick={() => setTab(sec.id)}
              title={sec.title || sec.label}
            >
              <span className="v-rail-glyph">{sec.glyph}</span>
              <span className="v-rail-label">{sec.label}</span>
            </button>
          ))}
          <div className="v-rail-divider" />
          <button className="v-rail-btn" onClick={() => s.setPage("start")} title="Your saved logos">
            <span className="v-rail-glyph">♥</span>
            <span className="v-rail-label">Your Logos</span>
          </button>
        </nav>

        {/* Middle: section controls */}
        <aside className="panel v2">
          <div className="panel-body">
            {tab === "preview" && <PreviewPanel svg={svg} />}

            {tab === "suggested" && <IdeasPanel />}

            {tab === "more-ideas" && <MoreIdeasPanel />}

            {tab === "layout" && (
              <>
                <div className="section-label">Layout</div>
                <div className="layout-grid">
                  {LAYOUTS.map((l) => (
                    <button
                      key={l.id}
                      className={`layout-tile ${s.layout === l.id ? "active" : ""}`}
                      onClick={() => { s.pushHistory(); s.setStyle({ layout: l.id }); }}
                    >
                      <span>{l.label}</span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {tab === "palette" && (() => {
              const currentIcon = s.iconId ? ICONS.find((i) => i.id === s.iconId) : undefined;
              const showAccent = currentIcon?.usesAccent === true;
              return (
                <>
                  <div className="section-label">Palette</div>
                  <ColorRow label="Title" color={s.titleColor} onChange={(c) => { s.pushHistory(); s.setStyle({ titleColor: c }); }} />
                  <ColorRow label="Slogan" color={s.sloganColor} onChange={(c) => { s.pushHistory(); s.setStyle({ sloganColor: c }); }} />
                  <ColorRow label="Icon" color={s.iconColor} onChange={(c) => { s.pushHistory(); s.setStyle({ iconColor: c }); }} />
                  {showAccent && (
                    <ColorRow
                      label="Icon accent"
                      color={s.iconAccent}
                      onChange={(c) => { s.pushHistory(); s.setStyle({ iconAccent: c }); }}
                    />
                  )}
                  <PaletteFromImage />
                </>
              );
            })()}

            {tab === "background" && (
              <>
                <div className="section-label">Background</div>
                <div style={{ fontSize: 12, color: "#475569", marginBottom: 14, lineHeight: 1.5 }}>
                  The canvas background is always transparent — your PNG / SVG exports keep their alpha so you can drop the logo on any surface. Use the <strong>Logo frame</strong> below to add a backdrop shape (circle, pill, rounded, square) that ships with the logo.
                </div>

                <div className="section-label" style={{ marginTop: 4 }}>Logo frame</div>
                <div style={{ fontSize: 12, color: "#475569", marginBottom: 10, lineHeight: 1.5 }}>
                  A backdrop shape that wraps the whole logo (icon + text). Canvas stays transparent — only the frame ships with your brand kit.
                </div>
                <div className="container-grid">
                  {([
                    { id: "none",               label: "None"               },
                    { id: "circle",             label: "Circle"             },
                    { id: "square",             label: "Square"             },
                    { id: "rounded",            label: "Square rounded"     },
                    { id: "rectangle",          label: "Rectangle"          },
                    { id: "rectangle-rounded",  label: "Rectangle rounded"  },
                    { id: "pill",               label: "Pill"               },
                  ] as const).map((f) => (
                    <button
                      key={f.id}
                      className={`container-tile ${s.logoFrame === f.id ? "active" : ""}`}
                      onClick={() => { s.pushHistory(); s.setStyle({ logoFrame: f.id }); }}
                      title={f.label}
                    >
                      <span className="container-preview">
                        {f.id === "none" && <span style={{ fontSize: 11, color: "#94a3b8" }}>—</span>}
                        {f.id === "circle"             && <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /></svg>}
                        {f.id === "square"             && <svg viewBox="0 0 24 24"><rect x="2"  y="2"  width="20" height="20" /></svg>}
                        {f.id === "rounded"            && <svg viewBox="0 0 24 24"><rect x="2"  y="2"  width="20" height="20" rx="4" /></svg>}
                        {f.id === "rectangle"          && <svg viewBox="0 0 24 24"><rect x="1"  y="7"  width="22" height="10" /></svg>}
                        {f.id === "rectangle-rounded"  && <svg viewBox="0 0 24 24"><rect x="1"  y="7"  width="22" height="10" rx="2" /></svg>}
                        {f.id === "pill"               && <svg viewBox="0 0 24 24"><rect x="1"  y="8"  width="22" height="8"  rx="4" /></svg>}
                      </span>
                      <span>{f.label}</span>
                    </button>
                  ))}
                </div>
                {s.logoFrame !== "none" && (
                  <>
                    <ColorRow
                      label="Frame color"
                      color={s.logoFrameColor}
                      onChange={(c) => { s.pushHistory(); s.setStyle({ logoFrameColor: c }); }}
                    />
                    <Slider
                      label="Frame width"
                      min={0.2} max={1} step={0.02}
                      value={s.logoFrameWidth}
                      onChange={(v) => s.setStyle({ logoFrameWidth: v })}
                      onCommit={() => s.pushHistory()}
                    />
                    <Slider
                      label="Frame height"
                      min={0.2} max={1} step={0.02}
                      value={s.logoFrameHeight}
                      onChange={(v) => s.setStyle({ logoFrameHeight: v })}
                      onCommit={() => s.pushHistory()}
                    />

                    <div className="section-label" style={{ marginTop: 18 }}>Frame border</div>
                    <Slider
                      label="Border width"
                      min={0} max={30} step={1}
                      value={s.logoFrameBorderWidth}
                      onChange={(v) => s.setStyle({ logoFrameBorderWidth: v })}
                      onCommit={() => s.pushHistory()}
                    />
                    {s.logoFrameBorderWidth > 0 && (
                      <>
                        <ColorRow
                          label="Border color"
                          color={s.logoFrameBorderColor}
                          onChange={(c) => { s.pushHistory(); s.setStyle({ logoFrameBorderColor: c }); }}
                        />
                        <div className="container-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
                          {([
                            { id: "solid",  label: "Solid"  },
                            { id: "dashed", label: "Dashed" },
                            { id: "dotted", label: "Dotted" },
                          ] as const).map((b) => (
                            <button
                              key={b.id}
                              className={`container-tile ${s.logoFrameBorderStyle === b.id ? "active" : ""}`}
                              onClick={() => { s.pushHistory(); s.setStyle({ logoFrameBorderStyle: b.id }); }}
                              title={b.label}
                            >
                              <span className="container-preview">
                                <svg viewBox="0 0 24 24">
                                  <line
                                    x1="2" y1="12" x2="22" y2="12"
                                    stroke="currentColor"
                                    strokeWidth="3"
                                    strokeLinecap={b.id === "dotted" ? "round" : "butt"}
                                    strokeDasharray={
                                      b.id === "dashed" ? "5 3"
                                      : b.id === "dotted" ? "0 5"
                                      : undefined
                                    }
                                  />
                                </svg>
                              </span>
                              <span>{b.label}</span>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            )}

            {tab === "name" && (
              <>
                <div className="section-label">Name</div>
                <Field label="Company Name">
                  <input type="text" value={s.title} onChange={(e) => s.setTitle(e.target.value)} onBlur={() => s.pushHistory()} />
                </Field>
                <Field label="Font">
                  <FontPicker value={s.titleFont} onChange={(v) => { s.pushHistory(); s.setStyle({ titleFont: v }); }} />
                </Field>
                <Slider label="Size" min={24} max={200} step={1} value={s.titleSize} onChange={(v) => s.setStyle({ titleSize: v })} onCommit={() => s.pushHistory()} />
                <ColorRow
                  label="Title color"
                  color={s.titleColor}
                  onChange={(c) => { s.pushHistory(); s.setStyle({ titleColor: c }); }}
                />
                <Style3DPicker
                  selected={s.titleStyle3D}
                  font={s.titleFont}
                  color={s.titleColor}
                  background={s.background}
                  onPick={(id) => { s.pushHistory(); s.setStyle({ titleStyle3D: id }); }}
                />
                {(s.titleStyle3D === "stacked" || s.titleStyle3D === "extruded") && (
                  <Slider label="Depth" min={1} max={30} step={1} value={s.titleDepth3D}
                          onChange={(v) => s.setStyle({ titleDepth3D: v })}
                          onCommit={() => s.pushHistory()} />
                )}
                <Slider label="Outline" min={0} max={20} step={1} value={s.titleStroke3D}
                        onChange={(v) => s.setStyle({ titleStroke3D: v })}
                        onCommit={() => s.pushHistory()} />
                <Slider label="Italic skew" min={-20} max={0} step={1} value={s.titleSkew}
                        onChange={(v) => s.setStyle({ titleSkew: v })}
                        onCommit={() => s.pushHistory()} />

                <div className="section-label" style={{ marginTop: 18, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span>Position</span>
                  {(s.titleOffsetX !== 0 || s.titleOffsetY !== 0) && (
                    <button
                      type="button"
                      className="btn ghost"
                      style={{ fontSize: 11, padding: "2px 8px" }}
                      onClick={() => { s.pushHistory(); s.setStyle({ titleOffsetX: 0, titleOffsetY: 0 }); }}
                      title="Reset name position"
                    >
                      Reset
                    </button>
                  )}
                </div>
                <div style={{ fontSize: 12, color: "#475569", marginBottom: 8, lineHeight: 1.5 }}>
                  Nudge the name independently of the auto-layout — left/right or up/down.
                </div>
                <Slider label="Horizontal (← →)" min={-200} max={200} step={1}
                        value={s.titleOffsetX}
                        onChange={(v) => s.setStyle({ titleOffsetX: v })}
                        onCommit={() => s.pushHistory()} />
                <Slider label="Vertical (↑ ↓)" min={-200} max={200} step={1}
                        value={s.titleOffsetY}
                        onChange={(v) => s.setStyle({ titleOffsetY: v })}
                        onCommit={() => s.pushHistory()} />
              </>
            )}

            {tab === "slogan" && (
              <>
                <div className="section-label">Slogan</div>
                <Field label="Slogan">
                  <input type="text" value={s.slogan} onChange={(e) => s.setSlogan(e.target.value)} onBlur={() => s.pushHistory()} placeholder="Add a Slogan (Optional)" />
                </Field>
                <Field label="Font">
                  <FontPicker value={s.sloganFont} onChange={(v) => { s.pushHistory(); s.setStyle({ sloganFont: v }); }} />
                </Field>
                <Slider label="Size" min={10} max={48} step={1} value={s.sloganSize} onChange={(v) => s.setStyle({ sloganSize: v })} onCommit={() => s.pushHistory()} />
                <ColorRow
                  label="Slogan color"
                  color={s.sloganColor}
                  onChange={(c) => { s.pushHistory(); s.setStyle({ sloganColor: c }); }}
                />
                <Style3DPicker
                  selected={s.sloganStyle3D}
                  font={s.sloganFont}
                  color={s.sloganColor}
                  background={s.background}
                  onPick={(id) => { s.pushHistory(); s.setStyle({ sloganStyle3D: id }); }}
                />
                {(s.sloganStyle3D === "stacked" || s.sloganStyle3D === "extruded") && (
                  <Slider label="Depth" min={1} max={30} step={1} value={s.sloganDepth3D}
                          onChange={(v) => s.setStyle({ sloganDepth3D: v })}
                          onCommit={() => s.pushHistory()} />
                )}
                <Slider label="Outline" min={0} max={20} step={1} value={s.sloganStroke3D}
                        onChange={(v) => s.setStyle({ sloganStroke3D: v })}
                        onCommit={() => s.pushHistory()} />
                <Slider label="Italic skew" min={-20} max={0} step={1} value={s.sloganSkew}
                        onChange={(v) => s.setStyle({ sloganSkew: v })}
                        onCommit={() => s.pushHistory()} />

                <div className="section-label" style={{ marginTop: 18, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span>Spacing</span>
                  {(s.titleSloganGap !== 12 || s.sloganOffsetX !== 0) && (
                    <button
                      type="button"
                      className="btn ghost"
                      style={{ fontSize: 11, padding: "2px 8px" }}
                      onClick={() => { s.pushHistory(); s.setStyle({ titleSloganGap: 12, sloganOffsetX: 0 }); }}
                      title="Reset spacing to defaults"
                    >
                      Reset
                    </button>
                  )}
                </div>
                <div style={{ fontSize: 12, color: "#475569", marginBottom: 8, lineHeight: 1.5 }}>
                  Push the slogan further from the title (or pull it closer / behind) and nudge it sideways.
                </div>
                <Slider label="Gap from title" min={-50} max={200} step={1}
                        value={s.titleSloganGap}
                        onChange={(v) => s.setStyle({ titleSloganGap: v })}
                        onCommit={() => s.pushHistory()} />
                <Slider label="Horizontal nudge" min={-200} max={200} step={1}
                        value={s.sloganOffsetX}
                        onChange={(v) => s.setStyle({ sloganOffsetX: v })}
                        onCommit={() => s.pushHistory()} />

                <SloganSuggestionsInline />
              </>
            )}

            {tab === "symbol" && (
              <>
                <div className="section-label">Symbol</div>
                <div className="cat-row">
                  {ICON_CATEGORIES.filter((c) => ICONS.some((i) => i.category === c)).map((c) => (
                    <button key={c} className={`chip ${iconCat === c ? "active" : ""}`} onClick={() => setIconCat(c)}>{c}</button>
                  ))}
                  <button className={`chip ${iconCat === "custom" ? "active" : ""}`} onClick={() => setIconCat("custom")}>custom</button>
                  <button className={`chip ${iconCat === "qr" ? "active" : ""}`} onClick={() => setIconCat("qr")}>QR</button>
                  <button className="chip danger" onClick={() => { s.pushHistory(); s.setIcon(null); }} title="No icon">none</button>
                </div>

                {iconCat !== "custom" && iconCat !== "qr" && (
                  <div className="icon-grid">
                    {ICONS.filter((i) => i.category === iconCat).map((i) => (
                      <button
                        key={i.id}
                        className={`icon-tile ${s.iconId === i.id ? "active" : ""}`}
                        onClick={() => {
                          s.pushHistory();
                          s.setIcon(i.id);
                          // If a cartoon is picked while no Logo frame is set, give it a
                          // white rounded backdrop so the sticker artwork has a solid surface.
                          if (i.pngUrl && (!s.logoFrame || s.logoFrame === "none")) {
                            s.setStyle({ logoFrame: "rectangle-rounded", logoFrameColor: "#ffffff" });
                          }
                        }}
                        title={i.name}
                      >
                        {i.pngUrl ? (
                          <img src={i.pngUrl} width={40} height={40} alt={i.name} style={{ objectFit: "contain", display: "block" }} />
                        ) : (
                          <svg viewBox="0 0 100 100" width="36" height="36">
                            {i.paths.map((p, idx) => {
                              if (typeof p === "string") {
                                // Reflect the user's picked icon colour so the thumbnail
                                // shows what they'll actually get. Falls back to dark navy
                                // only when the picked colour would be too pale to see
                                // against the white tile (luminance > 0.85).
                                const lit = relativeLuminance(s.iconColor);
                                const fill = lit > 0.85 ? "#0f172a" : s.iconColor;
                                return <path key={idx} d={p} fill={fill} fillRule={i.fillRule} />;
                              }
                              const fill = p.fill === "accent" ? s.iconAccent
                                : p.fill === "primary" || !p.fill ? s.iconColor
                                : p.fill;
                              return <path key={idx} d={p.d} fill={fill} fillRule={p.fillRule ?? i.fillRule} />;
                            })}
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {iconCat === "custom" && <CustomIconsPanel />}
                {iconCat === "qr" && <QrCodePanel />}

                <Slider label="Icon scale" min={0.4} max={2.0} step={0.05} value={s.iconScale} onChange={(v) => s.setStyle({ iconScale: v })} onCommit={() => s.pushHistory()} />
                <Slider
                  label="Distance from text"
                  min={-50} max={300} step={1}
                  value={s.iconTextGap}
                  onChange={(v) => s.setStyle({ iconTextGap: v })}
                  onCommit={() => s.pushHistory()}
                />

                {/* Inline color pickers — change symbol color without leaving the Symbol tab. */}
                {(() => {
                  const currentIcon = s.iconId ? ICONS.find((i) => i.id === s.iconId) : undefined;
                  const showAccent = currentIcon?.usesAccent === true;
                  const isRaster = !!currentIcon?.pngUrl;
                  if (isRaster) return null; // raster cartoons don't use icon colors
                  return (
                    <>
                      <div className="section-label" style={{ marginTop: 16 }}>Symbol color</div>
                      <ColorRow
                        label="Icon color"
                        color={s.iconColor}
                        onChange={(c) => { s.pushHistory(); s.setStyle({ iconColor: c }); }}
                      />
                      {showAccent && (
                        <ColorRow
                          label="Icon accent"
                          color={s.iconAccent}
                          onChange={(c) => { s.pushHistory(); s.setStyle({ iconAccent: c }); }}
                        />
                      )}
                    </>
                  );
                })()}

                <IconPatternPanel />
              </>
            )}

            {tab === "container" && <ContainerPanel />}

            {tab === "history" && <HistoryPanel />}

            {tab === "score" && <ScorePanel />}
          </div>
        </aside>

        {/* Right: live preview */}
        <main className="canvas-wrap">
          <button
            className={`golden-toggle ${goldenRatio ? "active" : ""}`}
            onClick={() => setGoldenRatio((v) => !v)}
            title="Toggle golden ratio guide (φ)"
            aria-pressed={goldenRatio}
          >φ</button>
          <div className="canvas-stack">
            <div className="canvas" dangerouslySetInnerHTML={{ __html: svg }} />
            {goldenRatio && <GoldenRatioOverlay />}
          </div>
          <div className="canvas-hint">Live preview · 500×500{goldenRatio ? " · φ guide on" : ""}</div>
        </main>
      </div>
      {saveModalOpen && (
        <SaveAsDesignModal
          defaultName={s.title || "My design"}
          onClose={() => setSaveModalOpen(false)}
        />
      )}
    </div>
  );
}

// ============================================================
// Save-as-design modal: name + public/private chooser.
// Public saves require sign-in (button kicks back to AccountFlyout flow).
// ============================================================
function SaveAsDesignModal({ defaultName, onClose }: { defaultName: string; onClose: () => void }) {
  const saveCurrentAsTemplate = useLogoStore((st) => st.saveCurrentAsTemplate);
  const attachFirestoreDocId = useLogoStore((st) => st.attachFirestoreDocId);
  const myTemplates = useLogoStore((st) => st.myTemplates);
  const { user, loading: authLoading } = useAuth();

  const [name, setName] = useState(defaultName);
  const [visibility, setVisibility] = useState<"public" | "private">("private");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    setError(null);
    if (visibility === "public" && !user) {
      setError("Sign in (top-right of the home page) before publishing publicly.");
      return;
    }
    setBusy(true);
    try {
      // 1. Always save locally first — instant, works offline, no Firestore needed.
      const localId = saveCurrentAsTemplate(name, visibility);

      // 2. If public, also publish to Firestore so other visitors see it.
      if (visibility === "public") {
        const justSaved = useLogoStore.getState().myTemplates.find((t) => t.id === localId)
          ?? myTemplates[0];
        if (justSaved) {
          const docId = await publishPublicLogo(justSaved);
          attachFirestoreDocId(localId, docId);
        }
      }
      onClose();
    } catch (err) {
      setError((err as Error)?.message || "Save failed. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="save-modal-overlay" onClick={onClose}>
      <div className="save-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Save design</h3>

        <label className="save-field">
          <span>Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="My design"
            autoFocus
          />
        </label>

        <div className="save-field">
          <span>Who can see it?</span>
          <div className="save-vis-options">
            <label className={`save-vis-opt ${visibility === "private" ? "active" : ""}`}>
              <input
                type="radio"
                name="vis"
                checked={visibility === "private"}
                onChange={() => setVisibility("private")}
              />
              <span className="save-vis-label">
                <strong>🔒 Private</strong>
                <small>Only you. Stays in your browser.</small>
              </span>
            </label>
            <label className={`save-vis-opt ${visibility === "public" ? "active" : ""}`}>
              <input
                type="radio"
                name="vis"
                checked={visibility === "public"}
                onChange={() => setVisibility("public")}
              />
              <span className="save-vis-label">
                <strong>🌐 Public</strong>
                <small>Shows in the community gallery on logoman.pro.</small>
              </span>
            </label>
          </div>
        </div>

        {visibility === "public" && !authLoading && !user && (
          <div className="save-modal-hint">
            You'll need to sign in (top-right of the home page) before publishing publicly.
          </div>
        )}

        {error && <div className="save-modal-error">{error}</div>}

        <div className="save-modal-actions">
          <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={handleSave} disabled={busy || !name.trim()}>
            {busy ? "Saving…" : visibility === "public" ? "Publish" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}


